// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@ces/App/Components/Notify/Notify";
import Tooltip, { TooltipPresentationProps } from "@ces/MobiusOverrides/Tooltip/Tooltip";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@ces/Overrides/Components/ProductPartNumbers";
import CartLineQuantity, { CartLineQuantityStyles } from "@ces/Overrides/Widgets/Cart/CartLineQuantity";
import AlertWarning from "@ces/XDL/Icons/AlertWarning";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { IconMemo, IconPresentationProps, IconProps } from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";

interface OwnProps {
    cartLine: CartLineModel;
    errorMessages?: React.ReactNode[];
    hideTippy: () => void;
    cart?: Readonly<Cart>;
    qtyOrderedChangeHandler: (cartLine: CartLineModel, qtyOrdered: number) => void;
    removeCartLineClickHandler: (cartLine: CartLineModel) => void;
    canSeePrices?: boolean;
}

interface MiniCartLineStyles {
    cartLine?: InjectableCss;
    imageWrapper?: InjectableCss;
    cartLineImage?: LazyImageProps;
    detailsWrapper?: InjectableCss;
    partNumbersStyles?: ProductPartNumbersStyles;
    lineDescription?: LinkPresentationProps;
    productInfoWrapper?: InjectableCss;
    qtyAndPriceWrapper?: InjectableCss;
    cartLineQuantityStyles?: CartLineQuantityStyles;
    basicProductPriceStyles?: ProductPriceStyles;
    extendedNetPriceValue?: TypographyProps;
    removeCartLineLink?: LinkPresentationProps;
    errorMessagesWrapper?: InjectableCss;
    warningIcon?: IconPresentationProps;
    specialOrderTooltip?: TooltipPresentationProps;
    qtyContainer?: InjectableCss;
    loginMessage?: TypographyPresentationProps;
    loginLink?: LinkPresentationProps;
    alertWarningIconStyles?: IconProps;
    warningToolTip?: TooltipPresentationProps;
}

export const miniCartLineStyles: MiniCartLineStyles = {};
const styles = miniCartLineStyles;

type Props = OwnProps;

const MiniCartLineCard: FC<Props> = ({
    cartLine,
    errorMessages,
    hideTippy,
    cart,
    qtyOrderedChangeHandler,
    removeCartLineClickHandler,
    canSeePrices,
}) => {
    return (
        <CartLineContext.Provider value={cartLine} data-test-selector={`miniCartLine_${cartLine.id}`}>
            <StyledWrapper {...styles.cartLine} data-test-selector="cartLine">
                <StyledWrapper {...styles.imageWrapper}>
                    <Link href={cartLine.productUri} onClick={hideTippy}>
                        <LazyImage
                            src={cartLine.smallImagePath}
                            altText={cartLine.altText || cartLine.shortDescription}
                            {...styles.cartLineImage}
                        />
                    </Link>
                    {cartLine.isRestricted && (
                        <Tooltip
                            text={siteMessage("Custom_Cart_ProductCantBePurchased")}
                            triggerComponent={<IconMemo {...styles.alertWarningIconStyles} src={AlertWarning} />}
                            appendTo={document.body}
                            {...styles.warningToolTip}
                        />
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.detailsWrapper}>
                    <Link href={cartLine.productUri} onClick={hideTippy} {...styles.lineDescription}>
                        {cartLine.shortDescription}
                    </Link>
                    <ProductPartNumbers productNumber={cartLine.erpNumber} extendedStyles={styles.partNumbersStyles} />
                    <StyledWrapper {...styles.qtyAndPriceWrapper}>
                        <StyledWrapper {...styles.qtyContainer}>
                            <CartLineQuantity
                                cart={cart as Cart}
                                editable={true}
                                extendedStyles={styles.cartLineQuantityStyles}
                                onQtyOrderedChange={(inputQty: number) => {
                                    qtyOrderedChangeHandler?.(cartLine, inputQty);
                                }}
                            />
                        </StyledWrapper>
                        {/* This may moved to be a component */}
                        {canSeePrices ? (
                            !cart?.cartNotPriced && (
                                <ProductPrice
                                    showLabel={false}
                                    showSavings={false}
                                    product={cartLine}
                                    extendedStyles={styles.basicProductPriceStyles}
                                />
                            )
                        ) : (
                            <Typography {...styles.loginMessage}>
                                {siteMessage("Custom_CartLine_Pricing_SignInForPrice")}
                            </Typography>
                        )}
                    </StyledWrapper>
                    {errorMessages && (
                        <StyledWrapper {...styles.errorMessagesWrapper}>
                            {errorMessages.map(message => (
                                <Notify
                                    type="danger"
                                    data-test-selector="cartline_errorMessage"
                                    key={`error_${cartLine.id}`}
                                >
                                    {message}
                                </Notify>
                            ))}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
                <Link
                    onClick={() => {
                        removeCartLineClickHandler(cartLine);
                    }}
                    data-test-selector="cartline_removeLine"
                    {...styles.removeCartLineLink}
                />
            </StyledWrapper>
        </CartLineContext.Provider>
    );
};

export default MiniCartLineCard;
